/**
 * @bos/portal3 — THE JOURNAL ROOM (`/app/runs/:id/journal`): the full thread, demoted.
 *
 * This used to be the middle of the run page — the first thing a person saw, and on a long
 * build it was 7,201 items of which 5,886 were raw machine log: absolute paths, tool
 * output, upstream vendor errors, in monospace, to a paying non-engineer. The good cards
 * were outnumbered more than four to one.
 *
 * Two things fix that, and only two. The Overview now carries the clean story (the
 * milestones), so this room does not have to be the story. And THE RAW LOG IS COLLAPSED
 * BY DEFAULT, one toggle per stretch — never deleted, never edited, and never presented as
 * if it were written for the reader. A closed stretch is not even rendered: on the long
 * build that is five thousand rows of machinery the browser is spared and the person did
 * not ask for.
 *
 * What this room is FOR is stated at the top, so nobody has to guess why it looks like
 * this: it is the record — every frame the machine wrote, in order, with nothing removed.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PATHS } from '../../routes';
import { useRunRoom } from '../roomContext';
import { groupThreadItems, threadItemFor, type ThreadItem } from '../model';

function Card({ item, runId }: { item: ThreadItem; runId: string }) {
  if (item.title === undefined) {
    return (
      <div className={`titem titem--${item.tone}`}>
        <span className="titem__body">{item.body}</span>
      </div>
    );
  }
  return (
    <div className={`titem titem--${item.tone}`}>
      <span className="titem__title">{item.title}</span>
      {item.body !== undefined && item.body !== '' && (
        <span className="titem__body">{item.body}</span>
      )}
      {/* A round is ONE line here, and the line leads to where it can be answered — the
          front desk, which names the room each question is waiting in. */}
      {item.room !== undefined && (
        <Link
          className="titem__go"
          to={item.room === '' ? PATHS.run(runId) : PATHS.runRoom(runId, item.room)}
        >
          Answer them
        </Link>
      )}
    </div>
  );
}

/**
 * One stretch of raw machine log, folded. Closed is the default AND the cheap state —
 * nothing inside is rendered until a person asks for it, which on the long build is five
 * thousand rows the browser never builds.
 *
 * The per-stretch handle is deliberately tiny: a real build has ~1,900 stretches, and a
 * full sentence repeated 1,900 times down the page would be a second kind of noise in
 * place of the first. The room's own switch above turns the whole layer on at once.
 */
function LogStretch({ items, forceOpen }: { items: readonly ThreadItem[]; forceOpen: boolean }) {
  const [open, setOpen] = useState(false);
  const shown = open || forceOpen;
  return (
    <div className="jlog">
      {!forceOpen && (
        <button
          type="button"
          className="jlog__toggle"
          aria-expanded={shown}
          title={shown ? 'Hide the machine’s log' : 'Show the machine’s log'}
          onClick={() => setOpen((o) => !o)}
        >
          ··· {items.length} line{items.length === 1 ? '' : 's'} of machine log
        </button>
      )}
      {shown && (
        <div className="jlog__lines">
          {items.map((item) => (
            <div key={item.seq} className="titem titem--log titem--muted">
              <span className="titem__body">{item.body}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function JournalRoom() {
  const { view, runId } = useRunRoom();
  const [showLog, setShowLog] = useState(false);

  const items = view.records
    .map((record) => threadItemFor(record))
    .filter((item): item is ThreadItem => item !== null);
  const groups = groupThreadItems(items);
  const logLines = items.filter((i) => i.kind === 'log').length;

  // Keep the record pinned to its latest entry as frames arrive — the same behaviour the
  // thread has always had.
  const threadRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = threadRef.current;
    if (el !== null) el.scrollTop = el.scrollHeight;
  }, [view.records.length]);

  return (
    <section className="sr jrnl" aria-label="Journal">
      <div className="sr__head">
        <h2 className="sr__title">Journal</h2>
      </div>
      <p className="sr__lede">
        Everything this build did, in order, with nothing removed. It is the record, not
        the story — the story is on the Overview. The machine’s own log is folded away
        under each stretch: it is written by the machine for the machine, and it is here
        because it is true, not because it is for you.
      </p>
      <div className="jrnl__bar">
        {items.length > 0 && (
          <p className="sr__meta">
            {items.length.toLocaleString()} entr{items.length === 1 ? 'y' : 'ies'}
            {logLines > 0 && <> · {logLines.toLocaleString()} of them machine log</>}
          </p>
        )}
        {logLines > 0 && (
          <button
            type="button"
            className="btn btn--quiet btn--sm"
            aria-expanded={showLog}
            onClick={() => setShowLog((s) => !s)}
          >
            {showLog ? 'Hide' : 'Show'} the machine’s log — {logLines.toLocaleString()} line
            {logLines === 1 ? '' : 's'}
          </button>
        )}
      </div>

      <div className="jthread" ref={threadRef} aria-label="Thread">
        {items.length === 0 && (
          <p className="page__note">Nothing has happened yet — the machine is starting up.</p>
        )}
        {groups.map((group, i) =>
          group.kind === 'log' ? (
            <LogStretch key={`log-${group.items[0].seq}-${i}`} items={group.items} forceOpen={showLog} />
          ) : (
            group.items.map((item) => <Card key={item.seq} item={item} runId={runId} />)
          ),
        )}
      </div>
    </section>
  );
}
